import {
  Content,
  ContentHeader,
  CreateButton,
  Header,
  Page,
} from '@backstage/core-components';
import { configApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  CatalogFilterLayout,
  EntityLifecyclePicker,
  EntityListProvider,
  EntityProcessingStatusPicker,
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListPicker,
  EntityNamespacePicker,
} from '@backstage/plugin-catalog-react';
import React, { ReactNode, useEffect, useState } from 'react';
import {
  catalogPlugin,
  DefaultCatalogPageProps,
} from '@backstage/plugin-catalog';
import { CustomCatalogTable } from './CatalogTable/CatalogTable';
import { CustomEntityKindPicker } from './CustomEntityPicker';
import { PCLink } from '../components/ui/ProductCatalogDocumentation';
import { allowedEntityKindsList, DEFAULT_CATALOG_KIND } from './catalogKinds';
import CustomSelect from '../components/ui/Select';
import {
  contentpages,
  notification,
  supportButtonData,
} from '../utils/constants';
import useCustomEntities from '../hooks/useEntities';
import { Entity } from '@backstage/catalog-model';
import { Grid } from '@material-ui/core';
import { SupportButton } from './CatalogSupport';

export interface BaseCatalogPageProps {
  filters: ReactNode;
  content?: ReactNode;
  selectedKind?: string;
}

export function BaseCatalogPage(props: BaseCatalogPageProps) {
  const { filters, content = <CustomCatalogTable /> } = props;

  const orgName =
    useApi(configApiRef).getOptionalString('organization.name') ?? 'Backstage';
  const createComponentLink = useRouteRef(
    catalogPlugin.externalRoutes.createComponent,
  );

  return (
    <Page themeId="home">
      <Header title={`${orgName} Catalog`}>
        <PCLink />
      </Header>
      <Content>
        <ContentHeader title="">
          {props.selectedKind?.toLowerCase() === 'component' && (
            <CreateButton
              title="Create"
              to={createComponentLink && createComponentLink()}
            />
          )}
          <SupportButton items={supportButtonData}></SupportButton>
        </ContentHeader>
        <EntityListProvider>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>{filters}</CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>{content}</CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </Page>
  );
}

export function CustomCatalogIndexPage(props: DefaultCatalogPageProps) {
  const { getCustomEntitiesByKind } = useCustomEntities('Component');
  const { getCustomEntitiesByKind: resolvedProductList } =
    useCustomEntities('System');
  const [notificationContentList, setNotificationContentList] = useState({
    notificationList: [],
    contentTopics: [],
  });
  const [productListEntity, setProductListEntity] = useState<Entity[]>([]);
  const [selected, setSelected] = useState('');
  const [selectedBusinessType, setSelectedBusinessType] = useState('');
  const [isBusinessComponent, setIsBusinessComponent] = useState(false);
  const [selectedTopic_Product, setSelectedTopic_Product] = useState<string[]>(
    [],
  );
  const [kind, setKind] = useState('');
  useEffect(() => {
    const filterContent_Notification = async () => {
      const notificationProductList = (
        await getCustomEntitiesByKind()
      ).items?.filter(x => x.spec?.products && x.spec?.type === notification);
      const contentTopicList = (await getCustomEntitiesByKind()).items?.filter(
        x => x.spec?.topic && x.spec?.type === contentpages,
      );
      setNotificationContentList({
        contentTopics: contentTopicList as any,
        notificationList: notificationProductList as any,
      });
    };
    filterContent_Notification();
  }, [isBusinessComponent]);

  useEffect(() => {
    const getProductList = async () => {
      const product_List = (await resolvedProductList()).items;
      setProductListEntity(product_List);
    };
    getProductList();
  }, []);

  const handleSelected = (id: string) => {
    
    setSelected(id);
  };

  const handleIsBusinessComponentSelected = (value: boolean): boolean => {
    setIsBusinessComponent(value);
    return true;
  };

  function handleSelectedBusessType(value: any) {
    setSelectedBusinessType(value);
  }
  function handleSelectedTopic_Product(value: any) {
    
    const ids: string[] = [];
    if (value && Array.isArray(value)) {
      for (let record of value) {
        if (selectedBusinessType === notification) {
          const product = productListEntity.find(
            x => x.metadata.title === record,
          );
          ids.push(product?.metadata.name as string);
        }
        if(kind?.toLowerCase()==='capability'){
          const name=productListEntity.find(x=>x.metadata.title?.toLowerCase()===record?.toLowerCase())?.metadata.name as string;
          ids.push(name);
        }
        else {
          ids.push(record as string);
        }
      }
    }
    setSelectedTopic_Product(ids);
  }

  function clearBusinessTypeSelection() {
    setSelectedBusinessType('');
  }

  function setSelectedKind(kind: string) {
    setKind(kind);
  }

  const {
    actions,
    initiallySelectedFilter = 'all',
    initialKind = DEFAULT_CATALOG_KIND,
    emptyContent,
    ownerPickerMode,
  } = props;

  function DisplayProductDropDown(title: string) {
    return (
      <CustomSelect
        handleOnchange={handleSelectedTopic_Product}
        items={
          title && title?.toLowerCase() === 'topic'
            ? (notificationContentList.contentTopics
                .map((item: Entity) => {
                  return item.spec?.topic;
                })
                .filter((item, index, self) => {
                  return self.findIndex(x => x === item) === index;
                }) as [])
            : (productListEntity.map((item: Entity) => {
                return item.metadata.title;
              }) as [])
        }
        label={title!!}
        type="checkbox"
      />
    );
  }

  return (
    <BaseCatalogPage
      filters={
        <>
          <CustomEntityKindPicker
            allowedKinds={allowedEntityKindsList}
            initialFilter={initialKind}
          />

          {!isBusinessComponent && (
            <>
              <EntityTypePicker />

              {kind?.toLowerCase().toString() !== 'capability' ? (
                <>
                  <UserListPicker initialFilter={initiallySelectedFilter} />
                  <EntityOwnerPicker mode={ownerPickerMode} />
                  <EntityLifecyclePicker />
                  <EntityTagPicker />
                  <EntityProcessingStatusPicker />
                  <EntityNamespacePicker />
                </>
              ) : (
                DisplayProductDropDown('Products')
              )}
            </>
          )}
          <Grid alignItems="stretch">
            {isBusinessComponent && (
              <CustomSelect
                handleOnchange={handleSelectedBusessType}
                items={[contentpages, notification]}
                label="Type"
                type="default"
              />
            )}
            {selectedBusinessType === contentpages && DisplayProductDropDown('Topic')}
            {selectedBusinessType === notification && DisplayProductDropDown('Product')}
          </Grid>
        </>
      }
      content={
        <CustomCatalogTable
          isOwned={selected === 'owned'}
          actions={actions}
          isAll={selected === 'all'}
          handleRefreshSelection={clearBusinessTypeSelection}
          emptyContent={emptyContent}
          setSelected={handleSelected}
          SelectedBusinessComponentType={selectedBusinessType}
          handleIsBusinessComponentSelected={handleIsBusinessComponentSelected}
          businessComponentData={notificationContentList as any}
          productListEntity={productListEntity}
          selectedProduct_Topic={selectedTopic_Product}
          setSelectedFilter={setSelectedKind}
        />
      }
      selectedKind={kind}
    />
  );
}
